import React, { useEffect, useState } from "react";
import {
  getTestimonialsList,
  deleteTestimonial,
} from "../../../services/testimonialsService";
import {
  paginateData,
  calculateTotalPages,
  BootstrapTooltip,
} from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { AddTestimonial } from "./AddTestimonial";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import allImages from "../../../assets/images-import";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";

const TESTIMONIAL_TRUNCATE_LENGTH = 80;

const getTestimonialId = (item) => item.id ?? item.Id;

const getPersonName = (item) => item.personName || item.PersonName || "";

const getPersonDesignation = (item) =>
  item.personDesignation || item.PersonDesignation || "-";

const getTestimonialText = (item) =>
  item.testimonialText || item.TestimonialText || "";

const getPersonImageUrl = (item) =>
  item.personImageUrl || item.PersonImageUrl || item.imageUrl || "";

const getDisplayOrder = (item) =>
  item.displayOrder ?? item.DisplayOrder ?? "-";

const truncateText = (text, maxLength = TESTIMONIAL_TRUNCATE_LENGTH) => {
  if (!text) return "-";
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

const renderTestimonialCell = (text) => {
  const fullText = text || "-";
  const display = truncateText(fullText);

  if (fullText === "-" || fullText.length <= TESTIMONIAL_TRUNCATE_LENGTH) {
    return display;
  }

  return (
    <BootstrapTooltip title={fullText}>
      <span style={{ cursor: "pointer" }}>{display}</span>
    </BootstrapTooltip>
  );
};

export const ManageTestimonialsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "testimonials";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [testimonialsList, setTestimonialsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getTestimonialsList();
      const list = response.data?.result || response.data || [];
      setTestimonialsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = testimonialsList.filter((item) => {
    const name = getPersonName(item).toLowerCase();
    const designation = getPersonDesignation(item).toLowerCase();
    const testimonial = getTestimonialText(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      name.includes(query) ||
      designation.includes(query) ||
      testimonial.includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (testimonialId) => {
    const confirmed = await confirmDelete("Testimonial");
    if (confirmed) {
      try {
        await deleteTestimonial(testimonialId);
        setTestimonialsList((prev) =>
          prev.filter((item) => getTestimonialId(item) !== testimonialId)
        );
        Swal.fire(
          "Deleted!",
          "The testimonial has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const renderPersonImageCell = (imageUrl) => (
    <img
      src={imageUrl || allImages.DefultImage}
      alt="Person"
      className="rounded img-thumbnail"
      style={{ width: "48px", height: "48px", objectFit: "cover" }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = allImages.DefultImage;
      }}
    />
  );

  return (
    <>
      <ComponentHeader title="Testimonials" />

      {pageAccessDetails.addAccess ? (
        <AddTestimonial
          editMode={editMode}
          initialData={selectedTestimonial ?? {}}
          onSuccess={fetchData}
          setSelectedTestimonial={setSelectedTestimonial}
          setEditMode={setEditMode}
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
        />
      ) : null}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5 xc-list-card">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h5 className="mb-0">Manage Testimonials</h5>
                  <span className="xc-list-count">
                    {filteredData.length}{" "}
                    {filteredData.length === 1
                      ? "testimonial"
                      : "testimonials"}
                  </span>
                </div>
                {pageAccessDetails.addAccess ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setSelectedTestimonial(null);
                      setEditMode(false);
                      setIsFormOpen(true);
                    }}
                  >
                    <i className="ri-add-line me-1" aria-hidden="true" />
                    Add Testimonial
                  </button>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search testimonials..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onRefresh={fetchData}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Person Image",
                          "Person Name",
                          "Designation",
                          "Testimonial",
                          "Display Order",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="8" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={getTestimonialId(item)}
                              rowData={{
                                slNo:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                personImage: renderPersonImageCell(
                                  getPersonImageUrl(item)
                                ),
                                personName: getPersonName(item),
                                designation: getPersonDesignation(item),
                                testimonial: renderTestimonialCell(
                                  getTestimonialText(item)
                                ),
                                displayOrder: getDisplayOrder(item),
                                status: renderStatusBadge(item),
                              }}
                              columns={[
                                "slNo",
                                "personImage",
                                "personName",
                                "designation",
                                "testimonial",
                                "displayOrder",
                                "status",
                              ]}
                              hideIcons={false}
                              onEdit={() => {
                                setSelectedTestimonial(item);
                                setEditMode(true);
                                setIsFormOpen(true);
                              }}
                              onDelete={() =>
                                handleDelete(getTestimonialId(item))
                              }
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
